import React, { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema, generateBreadcrumbSchema } from '../../utils/seo';

const TitleTransferServicePage: React.FC = () => {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [selectedScenario, setSelectedScenario] = useState<string>('purchase');
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://speedydmvservices.com';

  useSEO({
    title: 'Nevada Title Transfer Service Las Vegas - Expert Vehicle Title Help | Speedy DMV',
    description: 'Expert title transfer service in Las Vegas Nevada. Private party purchases, inheritances, gifts, and ownership changes. Complete document assistance and same-day processing. Licensed DMV provider. Call 702.979.7202.',
    keywords: 'title transfer service Las Vegas, Nevada vehicle title transfer help, car title service Las Vegas, DMV title transfer Nevada, title change service Las Vegas NV',
    canonical: `${siteUrl}/title-transfer-service`,
    ogImage: `${siteUrl}/img/speedy-dmv-services.jpg`,
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateLocalBusinessSchema(),
        generateServiceSchema(
          'Nevada Title Transfer Service',
          'Expert title transfer service in Las Vegas Nevada for private party purchases, inheritances, gifts, and ownership changes with complete documentation assistance.',
          '100'
        ),
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Services', url: '/services' },
          { name: 'Title Transfer Service', url: '/title-transfer-service' }
        ])
      ]
    }
  });

  const scenarios = {
    purchase: {
      title: 'Vehicle Purchase',
      documents: [
        'Current vehicle title (properly signed by seller)',
        'Bill of sale with purchase price',
        'Odometer disclosure (2011 or newer vehicles)',
        'Nevada Evidence of Insurance',
        'Valid Nevada driver license',
        'Lien release (if applicable)'
      ]
    },
    inheritance: {
      title: 'Inherited Vehicle',
      documents: [
        'Death certificate',
        'Probate documents or affidavit',
        'Current vehicle title',
        'Nevada Evidence of Insurance',
        'Valid identification',
        'Court orders (if applicable)'
      ]
    },
    gift: {
      title: 'Gifted Vehicle',
      documents: [
        'Gift affidavit or statement',
        'Current vehicle title (signed by giver)',
        'Nevada Evidence of Insurance',
        'Valid Nevada driver license',
        'Relationship verification (for family gifts)'
      ]
    }
  };

  const transferTypes = [
    {
      icon: 'ri-shopping-cart-line',
      title: 'Purchase Transfer',
      description: 'Complete title transfer for recently purchased vehicles from private parties or dealers.'
    },
    {
      icon: 'ri-gift-line',
      title: 'Gift Transfer',
      description: 'Process title transfers for vehicles received as gifts from family or friends.'
    },
    {
      icon: 'ri-heart-line',
      title: 'Inheritance Transfer',
      description: 'Handle title transfers for inherited vehicles with proper estate documentation.'
    },
    {
      icon: 'ri-exchange-line',
      title: 'Ownership Changes',
      description: 'Process changes in ownership due to divorce, marriage, or business transfers.'
    }
  ];

  const toggleExpanded = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-32">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-green-900 via-green-800 to-green-900 text-white py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Nevada Vehicle Title Transfer Services
              </h1>
              <p className="text-xl mb-8 max-w-3xl mx-auto">
                Expert title transfer services for all vehicle ownership changes. We ensure proper documentation 
                and legal compliance for purchases, gifts, inheritance, and ownership modifications.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:702-979-7202" 
                   className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                  Call Now: (702) 979-7202
                </a>
                <a href="/appointment" 
                   className="bg-white text-green-900 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                  Schedule Appointment
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Service Types */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Title Transfer Services We Handle
              </h2>
              <p className="text-lg text-gray-600">
                Professional assistance for all types of vehicle title transfers in Nevada
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {transferTypes.map((type, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-md text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${type.icon} text-2xl text-green-600`}></i>
                  </div>
                  <h3 className="text-lg font-semibold mb-3">{type.title}</h3>
                  <p className="text-gray-600 text-sm">{type.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Document Requirements by Scenario */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Required Documents by Transfer Type
              </h2>
              <p className="text-lg text-gray-600">
                Select your situation to see the specific documents you'll need
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              {/* Scenario Selector */}
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                {Object.entries(scenarios).map(([key, scenario]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedScenario(key)}
                    className={`px-6 py-3 rounded-lg font-medium transition-colors whitespace-nowrap ${
                      selectedScenario === key 
                        ? 'bg-green-600 text-white' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {scenario.title}
                  </button>
                ))}
              </div>

              {/* Document List */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                  Documents for {scenarios[selectedScenario as keyof typeof scenarios].title}
                </h3>
                <div className="space-y-3">
                  {scenarios[selectedScenario as keyof typeof scenarios].documents.map((doc, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-sm text-green-600"></i>
                      </div>
                      <span className="text-gray-800">{doc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Important Information */}
        <section className="py-16 bg-yellow-50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="bg-white rounded-lg shadow-md p-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-information-line text-2xl text-yellow-600"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    New Federal Odometer Disclosure Law
                  </h3>
                  <div className="space-y-4 text-gray-700">
                    <p className="font-medium">
                      Important: Vehicles 2011 or newer require odometer disclosure regardless of what the title states.
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>If your vehicle is 2011 model year or newer, you MUST enter the current mileage</li>
                      <li>If your vehicle is 2010 model year or older, you may check the exempt box</li>
                      <li>A paper title is required for vehicles 2011 and newer - no bill of sale substitutions</li>
                      <li>The seller must provide accurate odometer reading even if title shows "exempt"</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Process Timeline */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Title Transfer Process
              </h2>
              <p className="text-lg text-gray-600">
                Our streamlined process ensures your title transfer is completed correctly and efficiently
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="space-y-8">
                {[
                  {
                    step: 1,
                    title: 'Document Review',
                    description: 'We review all your documents to ensure completeness and accuracy before processing.',
                    time: '15 minutes'
                  },
                  {
                    step: 2,
                    title: 'Verification Process',
                    description: 'Verify vehicle identification, ownership history, and lien information.',
                    time: '20 minutes'
                  },
                  {
                    step: 3,
                    title: 'DMV Processing',
                    description: 'Submit your application and documents to Nevada DMV for official processing.',
                    time: '30 minutes'
                  },
                  {
                    step: 4,
                    title: 'New Title Issued',
                    description: 'Receive your new Nevada title with updated ownership information.',
                    time: 'Same day'
                  }
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-6">
                    <div className="w-12 h-12 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                      {item.step}
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
                        <h3 className="text-xl font-semibold text-gray-900">{item.title}</h3>
                        <span className="text-sm font-medium text-green-600 bg-green-100 px-3 py-1 rounded-full">
                          {item.time}
                        </span>
                      </div>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Title Transfer Questions
              </h2>
            </div>

            <div className="space-y-4">
              {[
                {
                  question: "How long does a title transfer take?",
                  answer: "With Speedy DMV Services, most title transfers are completed the same day with proper documentation. Complex cases involving liens or estate issues may require additional processing time."
                },
                {
                  question: "What if there's a lien on the vehicle?",
                  answer: "If there's an existing lien, we'll need a lien release from the financial institution. We can help coordinate with lienholders to obtain the necessary documentation."
                },
                {
                  question: "Can I transfer a title without the seller present?",
                  answer: "Yes, as long as the seller has properly signed the title and provided all required documentation. The title must be signed correctly with accurate odometer disclosure."
                },
                {
                  question: "What happens if the title is damaged or lost?",
                  answer: "If the title is damaged or lost, the current owner must obtain a duplicate title from the state where it was issued before the transfer can be completed."
                },
                {
                  question: "Are there any tax implications for title transfers?",
                  answer: "Nevada may assess governmental services tax based on the vehicle's value. Gift transfers between immediate family members may qualify for tax exemptions."
                }
              ].map((faq, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md">
                  <button
                    className="w-full px-6 py-4 text-left flex items-center justify-between focus:outline-none"
                    onClick={() => toggleExpanded(`faq-${index}`)}
                  >
                    <span className="font-semibold text-gray-900">{faq.question}</span>
                    <i className={`ri-arrow-${expandedSection === `faq-${index}` ? 'up' : 'down'}-s-line text-xl text-gray-500`}></i>
                  </button>
                  {expandedSection === `faq-${index}` && (
                    <div className="px-6 pb-4">
                      <p className="text-gray-600">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-16 bg-green-900 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-4">
              Need Help with Title Transfer?
            </h2>
            <p className="text-xl mb-8">
              📞 Appointments Required – Please Call Ahead
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:702-979-7202" 
                 className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                Call (702) 979-7202
              </a>
              <a href="/required-documents" 
                 className="bg-white text-green-900 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                Document Checklist
              </a>
            </div>
            <div className="mt-6 text-gray-300">
              <p>Speedy DMV Services - 4525 S Sandhill Rd, Las Vegas, NV</p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default TitleTransferServicePage;
